import { useMemo } from 'react';
import classnames from 'classnames';
import {
  ExpandButton,
  ExpandAllButton,
} from 'components/common/data-table/expander-button-component';
import { EXPANDER_COLUMN_ID } from 'constants/common/data-table-constants';

/**
 * Custom hook for generating table columns
 * @param {Array} headers - Array of header configurations
 * @param {Object} options - Configuration options
 * @param {boolean} options.showExpander - Show expander column (default: true)
 * @param {number} options.indentSize - Indentation size in rem (default: 1)
 * @param {number} options.indentColumnIndex - Which column to indent (default: 0)
 * @param {Function} options.customCellRenderer - Custom cell renderer function
 */
export const useTableColumns = (headers = [], config = {}) => {
  const {
    showExpander = false,
    indentSize = 1,
    indentColumnIndex = 0,
    customCellRenderer,
    expanderIconSizeWidth = 10,
    expanderIconSizeHeight = 10,
  } = config;
  const EXPAND_ALL_BUTTON_SIZE = 16;

  return useMemo(() => {
    const columns = [];

    if (showExpander) {
      columns.push({
        id: EXPANDER_COLUMN_ID,
        header: ({ table }) => (
          <ExpandAllButton
            width={EXPAND_ALL_BUTTON_SIZE}
            height={EXPAND_ALL_BUTTON_SIZE}
            isExpanded={table.getIsAllRowsExpanded()}
            onClick={() => table.toggleAllRowsExpanded()}
          />
        ),
        cell: ({ row }) =>
          row.getCanExpand() && (
            <ExpandButton
              width={expanderIconSizeWidth}
              height={expanderIconSizeHeight}
              isExpanded={row.getIsExpanded()}
              onClick={row.getToggleExpandedHandler()}
            />
          ),
      });
    }

    // Add data columns
    const dataColumns = headers.map(
      ({ header, accessor, id, customCell }, index) => {
        const columnConfig = {
          header,
          id,
          accessorKey: accessor,
          cell: ({ row, getValue, table }) => {
            const cellValue = customCell ? customCell(row) : getValue();
            const value = cellValue?.value || cellValue;
            const icon = cellValue?.icon || null;
            const onCellClick = cellValue?.onClick || null;
            const selectionCount = cellValue?.selectionCount || null;
            const isSelected = cellValue?.isSelected || false;
            const cellHighlightClass = cellValue?.highlightClass || '';
            const highlightInfo = table.options.meta?.highlightedCellInfo;
            const highlightClass = table.options.meta?.highlightClassName;
            const shouldHighlightCell = cellValue?.shouldHighlightCell || false;
            const hasCellData =
              value != null && value !== '' && value !== undefined;
            const isHighlighted =
              hasCellData &&
              highlightInfo?.rowId === row.id &&
              (!highlightInfo.columnAccessor ||
                highlightInfo.columnAccessor === accessor);

            const content = customCellRenderer
              ? customCellRenderer(value, row)
              : value;

            const highlighted = isHighlighted ? (
              <span className={highlightClass}>{content}</span>
            ) : (
              content
            );

            const selectionCountBadge = selectionCount ? (
              <span className="data-table__cell--selection-count">
                {selectionCount}
              </span>
            ) : null;

            const clickableContent = onCellClick ? (
              <span
                className={classnames(
                  'data-table__cell--clickable',
                  {
                    'data-table__cell--selected': isSelected,
                  },
                  cellHighlightClass,
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onCellClick();
                }}
                role="button"
              >
                {highlighted}
                {selectionCountBadge}
              </span>
            ) : (
              highlighted
            );

            const cellContent = icon ? (
              <span className="text_icon_cell">
                {icon}
                {clickableContent}
              </span>
            ) : (
              clickableContent
            );

            return index === indentColumnIndex ? (
              <span style={{ marginLeft: `${row.depth * indentSize}rem` }}>
                {cellContent}
              </span>
            ) : (
              cellContent
            );
          },
        };

        return columnConfig;
      },
    );

    return [...columns, ...dataColumns];
  }, [
    headers,
    showExpander,
    indentSize,
    indentColumnIndex,
    customCellRenderer,
  ]);
};
